import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { from, Observable, of } from 'rxjs';
import { catchError, finalize, map, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuditTrail, DoubleBookingRequest, DoubleBookingResponse, DoubleBookingResponseV2, EOrder, ManualPaymentActionResponse, Reservation, Shift , PreauthListResponse, ManualReserveResponse, PaymentLinkExpirationGetResponse, PaymentLinkExpirationUpdateRequest } from '../model/reservation';
import * as AppActions from './../store/actions/app.actions';
import { NgxSpinnerService } from 'ngx-spinner';

export type TimeToSearch = 'PAST' | 'TODAY';
export type SearchOrdering = 'ASC' | 'DESC';

@Injectable({
  providedIn: 'root'
})
export class ReservationApiService {

  constructor(private httpClient: HttpClient, private translate: TranslateService, private store: Store<{}> , private spinner: NgxSpinnerService) { }

  private baseUrl = `${environment.apiUrl}/reservations`;
  private baseAuditTrailUrl = `${environment.auditTrailUrl}/ihost`;
  private baseReportUrl = `${environment.reportsApiUrl}/reservations`;

  getReservations(restaurantId: number | string, date: Date): Observable<any> { // { reservationsList: Reservation[], shifts: Shift[] }
    restaurantId = restaurantId.toString();
    const workingDate = moment(date).format('yyyy-MM-DD');

    return this.httpClient.post<{ reservationsList: Reservation[], shifts: Shift[] }>(`${this.baseUrl}/search/specificDate`, { restaurantId, workingDate }).pipe(
      map((res: { reservationsList: Reservation[], shifts: Shift[] }) => {
        return res;
      })
    );
  }

  getAuditTrails(reservationId: string): Observable<{ auditTrails: AuditTrail[] }> {
    return this.httpClient.get<{ auditTrails: AuditTrail[] }>(`${this.baseAuditTrailUrl}/AuditTrailInfo?reservationId=${reservationId}`).pipe(
      map((res: { auditTrails: AuditTrail[] }) => {
        return res;
      })
    );
  }

  createOrUpdateReservation(restaurantId: number | string, reservation: Reservation): Observable<any> {
    restaurantId = restaurantId.toString();
    return this.httpClient.post<any>(`${this.baseUrl}/update`, {
      restaurantId,
      affectedReservations: [reservation],
      language: "unknown"
    }).pipe(
      map((res: any) => {

        if (res?.forceLogout === true) {
          // todo: logout
          this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translate.instant('login.pleaseTryToLoginLater') });
        } else if (res?.ErrorCode != null && res?.ErrorCode.length > 0) {
          this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translate.instant('reservations.failedToSaveReservation') });
        }

        return res;
      })
    );
  }

  reservationSearch(
    curUserRestaurantId: number,
    restaurantIds: string[],
    searchTerm: string,
    date: string,
    timeToSearch: TimeToSearch,
    ordering: SearchOrdering): Observable<{ reservationsList: Reservation[] }> {
    return this.httpClient.post<{ reservationsList: Reservation[] }>(`${this.baseReportUrl}/globalsearch`, {
      curUserRestaurantId,
      restaurantIds,
      searchTerm,
      date,
      timeToSearch,
      ordering
    }).pipe(
      map((res: { reservationsList: Reservation[] }) => {
        return res;
      })
    );
  }

  refund(restaurantId: number | string, reservationId: string, price: number): Observable<ManualPaymentActionResponse> {
    restaurantId = restaurantId.toString();
    return this.httpClient.get<any>(`${environment.webServerUrl}/reservations/ManualRefund?rsvId=${reservationId}&restId=${restaurantId}&price=${price}`).pipe(
      map((res: ManualPaymentActionResponse) => {
        return res;
      })
    );
  }

  charge(restaurantId: number | string, reservationId: string, price: number): Observable<ManualPaymentActionResponse> {
    return this.httpClient.get<any>(`${environment.webServerUrl}/reservations/ManualCharge?rsvId=${reservationId}&restId=${restaurantId}&price=${price}`).pipe(
      map((res: ManualPaymentActionResponse) => {
        return res;
      })
    );
  }

reserve(restaurantId: number | string,reservationId: string,price: number): Observable<ManualReserveResponse> {
  restaurantId = restaurantId.toString();
  return this.httpClient.get<ManualReserveResponse>(
    `${environment.webServerUrl}/reservations/ManualReserve?rsvId=${reservationId}&restId=${restaurantId}&price=${price}`
  );
}

getPreauthStatus(restaurantId: number | string, reservationId: string): Observable<any> {
  restaurantId = restaurantId.toString();
  return this.httpClient.get<any>(
    `${environment.webServerUrl}/reservations/ManualPreauthStatus?rsvId=${reservationId}&restId=${restaurantId}`
  );
}

captureReserved(restaurantId: number | string, paymentIntentId: string, price?: number) {
  restaurantId = restaurantId.toString();
  const priceParam = (price !== undefined && price !== null) ? `&price=${price}` : '';
  return this.httpClient.get<any>(
    `${environment.webServerUrl}/reservations/ManualCaptureReserved?restId=${restaurantId}&paymentIntentId=${paymentIntentId}${priceParam}`
  );
}

releaseReserved(restaurantId: number | string, paymentIntentId: string) {
  restaurantId = restaurantId.toString();
  return this.httpClient.get<any>(
    `${environment.webServerUrl}/reservations/ManualReleaseReserved?restId=${restaurantId}&paymentIntentId=${paymentIntentId}`
  );
}

getPreauthList(restaurantId: number | string, reservationId: string): Observable<PreauthListResponse> {
  restaurantId = restaurantId.toString();
  return this.httpClient.get<PreauthListResponse>(
    `${environment.webServerUrl}/reservations/ManualPreauthList?rsvId=${reservationId}&restId=${restaurantId}`
  );
}

refundReserved(restaurantId: number | string, paymentIntentId: string) {
  restaurantId = restaurantId.toString();

  return this.httpClient.get<any>(
    `${environment.webServerUrl}/reservations/ManualRefundReserved?restId=${restaurantId}&paymentIntentId=${paymentIntentId}`
  );

}

  getOrderDetails(reservationId: string | null): Observable<EOrder> {        
    return this.httpClient.get<any>(`${environment.ordersApiUrl}/Order/GetByMetaData/${reservationId}`).pipe(
      map((res: any) => {        
        return res.entity;
      })
    );
  }

  getAvailability(payload: any): Observable<any>{ 
    return this.httpClient.post<any>(`${environment.apiUrl}/onlineAvailability/quickSearch`, payload);
  }


  updateAvailability(payload: any): Observable<any>{
    return this.httpClient.post<any>(`${environment.apiUrl}/onlineAvailability/quickClose`, payload);
  }

  changeRestaurant(reservation: Reservation, restaurantId: number | string): Observable<any> {
    restaurantId = restaurantId.toString();
    return this.httpClient.post<any>(`${this.baseUrl}/changeRestaurant`, {
      restaurantId,
      affectedReservations: [reservation],
      language: "unknown"
    }).pipe(
      map((res: any) => {

        if (res?.forceLogout === true) {
          // todo: logout
          this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translate.instant('login.pleaseTryToLoginLater') });
        } else if (res?.ErrorCode != null && res?.ErrorCode.length > 0) {
          this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translate.instant('reservations.failedToSaveReservation') });
        }

        return res;
      })
    );
  }

  doubleBookingCheck(request: DoubleBookingRequest): Observable<DoubleBookingResponse> {
    return this.httpClient.post<DoubleBookingResponse>(
      `${environment.reportsApiUrl}/Reservations/doubleBookingCheck`,
      request,
      {
        headers: { 'X-Bypass-Interceptor': 'true' }
      }
    ).pipe(
      catchError((err) => {
        console.warn('Double booking check error ignored:', err);
        return of({ conflict: false, matchedFields: [], success: false });
      })
    );
  }

  doubleBookingCheckV2(request: DoubleBookingRequest): Observable<DoubleBookingResponseV2> {
  return this.httpClient.post<DoubleBookingResponseV2>(
    `${environment.reportsApiUrl}/Reservations/doubleBookingCheckV2`,
    request,
    {
      headers: { 'X-Bypass-Interceptor': 'true' }
    }
  ).pipe(
    catchError((err) => {
      console.warn('Double booking check V2 error ignored:', err);
      return of({
        conflict: false,
        matchedFields: [],
        success: false,
        conflictReservations: []
      });
    })
  );
}

  getPaymentLinkExpiration(reservationId: string): Observable<PaymentLinkExpirationGetResponse> {
  return this.httpClient.post<PaymentLinkExpirationGetResponse>(
    `${this.baseReportUrl}/getPaymentLinkExpirationDate`,
    { reservationId }
  );
}

setPaymentLinkExpiration(req: PaymentLinkExpirationUpdateRequest): Observable<PaymentLinkExpirationGetResponse> {
  return this.httpClient.post<PaymentLinkExpirationGetResponse>(
    `${this.baseReportUrl}/setPaymentLinkExpirationDate`,
    {
      reservationId: req.reservationId,
      paymentLinkExpiresAt: req.paymentLinkExpiresAt
    }
  );
}


}
